import { ReactElement, useMemo } from 'react'
import {
  getPolygonNormal,
  getShapeMatrixTransform,
  getShapeObject,
  createMeshStandardMaterialMemoized,
  getShapeEdges,
} from '@scene'
import { DoubleSide, Vector2, Sprite, SpriteMaterial, CanvasTexture } from 'three'
import { LineMaterial } from 'three/addons/lines/LineMaterial.js'
import { LineSegments2 } from 'three/addons/lines/LineSegments2.js'
import { LineSegmentsGeometry } from 'three/addons/lines/LineSegmentsGeometry.js'
import { BasicMesh, getCenterMultipleReduceMean, sceneConfig } from '@modugen/ui-toolkit'
import { useDisposeThreeResourceOnUnmount } from '@editorHooks'
import { getWindLoadAreaTypeShort } from 'src/constants/windLoadAreaTypesShort'
import { getWindLoadAreaTypeColor } from 'src/constants/windLoadColors'

interface CPEAreaWithId extends CPEArea {
  id: string
}

interface Props {
  area: CPEAreaWithId
}

const WindLoadCPEAreaMeshElement = ({ area }: Props): ReactElement => {
  const createTextSprite = (text: string, color: string, backgroundColor?: string) => {
    const canvas = document.createElement('canvas')
    const context = canvas.getContext('2d')
    canvas.width = 256
    canvas.height = 256

    if (context) {
      // Set background only if provided
      if (backgroundColor) {
        context.fillStyle = backgroundColor
        context.fillRect(0, 0, canvas.width, canvas.height)
      }

      // Set text properties
      context.font = 'Bold 60px Arial'
      context.fillStyle = color
      context.textAlign = 'center'
      context.textBaseline = 'middle'
      context.fillText(text, 128, 128)
    }

    const texture = new CanvasTexture(canvas)
    const spriteMaterial = new SpriteMaterial({
      map: texture,
      transparent: true,
      alphaTest: 0.01, // This helps with transparency
    })
    const sprite = new Sprite(spriteMaterial)
    sprite.scale.set(1, 1, 1)

    return sprite
  }
  const points = area.points
  const matrixTransform = useMemo(() => getShapeMatrixTransform(points), [points])

  const shapeObject = useMemo(
    () => getShapeObject(points, [], matrixTransform),
    [points, matrixTransform],
  )

  const edges = useMemo(() => getShapeEdges(shapeObject, 0.0), [shapeObject])

  const lineGeometry = useMemo(() => {
    const lineSegmentsGeometry = new LineSegmentsGeometry()
    const positions = new Float32Array(edges.attributes.position.array)
    lineSegmentsGeometry.setPositions(positions)
    return lineSegmentsGeometry
  }, [edges])

  useDisposeThreeResourceOnUnmount(edges)
  useDisposeThreeResourceOnUnmount(lineGeometry)

  const norm = useMemo(() => getPolygonNormal(points), [points])

  // Create a small thickness for the area visualization
  const labelOffsetAlongNormal = 0.1

  const labelPosition = useMemo(
    () => getCenterMultipleReduceMean(points).addScaledVector(norm, labelOffsetAlongNormal),
    [points, norm, labelOffsetAlongNormal],
  )

  // Color based on load area type using seaborn-like color palettes
  const labelText = getWindLoadAreaTypeShort(area.load_area_type)
  const areaColor = getWindLoadAreaTypeColor(area.load_area_type)
  const textColor = 'black'

  // Create text sprite for the label
  const textSprite = useMemo(() => {
    return createTextSprite(labelText, textColor) // No background color = transparent
  }, [labelText, textColor])

  const material = useMemo(
    () =>
      createMeshStandardMaterialMemoized({
        side: DoubleSide,
        // @ts-ignore
        color: areaColor,
        opacity: 0.5,
        transparent: true,
        polygonOffset: true,
        polygonOffsetFactor: -1,
      }),
    [areaColor],
  )

  const lineMaterial = useMemo(
    () =>
      new LineMaterial({
        color: 'black',
        linewidth: 1.5, // in world units
        resolution: new Vector2(window.innerWidth, window.innerHeight), // resolution of the viewport
        opacity: 1.0,
      }),
    [],
  )

  useDisposeThreeResourceOnUnmount(material)
  useDisposeThreeResourceOnUnmount(lineMaterial)

  return (
    <>
      <BasicMesh
        name={area.id}
        // why disabling matrixAutoUpdate should be fine:
        // https://github.com/pmndrs/react-three-fiber/issues/635#issuecomment-682439509
        matrix={matrixTransform}
        matrixAutoUpdate={false}
        material={material}
        userData={{
          points,
        }}
        renderOrder={100}
      >
        <extrudeGeometry
          args={[
            shapeObject,
            {
              depth: 0,
              bevelEnabled: false,
            },
          ]}
        />
        <primitive
          object={new LineSegments2(lineGeometry, lineMaterial)}
          layers={sceneConfig.R3FNonSelectableObjectLayer}
          renderOrder={100}
        />
      </BasicMesh>
      {textSprite && <primitive object={textSprite} position={labelPosition.v} />}
    </>
  )
}

export default WindLoadCPEAreaMeshElement
